import {
  type CodeStatusResponseDto,
  generateCode as sdkGenerateCode,
  type GenerateCodeResponseDto,
  getCodeStatus as sdkGetCodeStatus,
  getSessionReadiness as sdkGetSessionReadiness,
  regenerateCode as sdkRegenerateCode,
  type SessionReadinessResponseDto,
  validateCode as sdkValidateCode,
  type ValidateCodeResponseDto,
} from "~/lib/api";
import { ApiExpiredError, ApiLockedError } from "~/lib/api";

/**
 * Network + mapping layer for the handover-code flow. Keeps the generated SDK
 * and the status-code → user-state translation out of the screens themselves
 * (the start-handover screen for E6.4, the home screen for E6.5), mirroring
 * `~/lib/patients.ts`. Each wrapper returns a discriminated union so the caller
 * stays in switch-form and the type checker enforces exhaustiveness.
 */

export type GenerateCodeResult =
  | { status: "ok"; data: GenerateCodeResponseDto }
  | { status: "error"; message: string };

const GENERIC_GENERATE_ERROR = "Something went wrong generating the code. Please try again.";

function messageForGenerateStatus(status: number): string {
  switch (status) {
    case 400:
      return "This handover has no patients yet. Add at least one patient before starting.";
    case 403:
      return "Only the doctor who started this handover can generate a code.";
    case 404:
      return "This session no longer exists, or you no longer have access to it.";
    case 409:
      // 409: the session already has a code (no longer a draft). The plaintext is
      // never recoverable, so the doctor must start a fresh session.
      return "This handover has already been started. The code is shown only once and can't be retrieved again.";
    default:
      return GENERIC_GENERATE_ERROR;
  }
}

/**
 * Generate the one-time handover code for a draft session (`POST /sessions/:id/code`).
 * The plaintext `code` comes back exactly once; the caller must keep it in
 * component state only and never persist it. The path param is `id` (the code
 * controller mounts at `sessions/:id/code`).
 */
export async function generateHandoverCode(sessionId: string): Promise<GenerateCodeResult> {
  try {
    const result = await sdkGenerateCode({ path: { id: sessionId } });
    if (result.error || !result.data) {
      return { status: "error", message: messageForGenerateStatus(result.response.status) };
    }
    return { status: "ok", data: result.data };
  } catch {
    return { status: "error", message: GENERIC_GENERATE_ERROR };
  }
}

/** The non-secret state of a session's code, for the start-handover page after a
 *  reload (HAN-89). `denied` is the outgoing-doctor gate (403); `not-found`
 *  collapses a missing session and a ward the caller can't access (404). */
export type CodeStatusResult =
  | { status: "ok"; data: CodeStatusResponseDto }
  | { status: "denied" }
  | { status: "not-found" }
  | { status: "error" };

/**
 * Read the current code state for a session (`GET /sessions/:id/code`). Returns
 * everything the start-handover page needs to render the expiry countdown and
 * the regenerate affordance after a reload — never the plaintext, which is gone
 * for good once shown.
 */
export async function fetchCodeStatus(sessionId: string): Promise<CodeStatusResult> {
  try {
    const result = await sdkGetCodeStatus({ path: { id: sessionId } });
    if (result.data && !result.error) {
      return { status: "ok", data: result.data };
    }
    if (result.response.status === 403) return { status: "denied" };
    if (result.response.status === 404) return { status: "not-found" };
    return { status: "error" };
  } catch {
    return { status: "error" };
  }
}

/** The handover-readiness checklist for a session (HAN-64). `denied` is the
 *  outgoing-doctor gate (403); `not-found` collapses a missing session and a
 *  ward the caller can't access (404). */
export type SessionReadinessResult =
  | { status: "ok"; data: SessionReadinessResponseDto }
  | { status: "denied" }
  | { status: "not-found" }
  | { status: "error" };

/**
 * Read the pre-generation readiness checklist for a session
 * (`GET /sessions/:id/readiness`). Drives the final summary the outgoing doctor
 * sees before starting the handover, and which patients (if any) still block code
 * generation. The server enforces the same `ready` rule when a code is requested.
 */
export async function fetchSessionReadiness(sessionId: string): Promise<SessionReadinessResult> {
  try {
    const result = await sdkGetSessionReadiness({ path: { id: sessionId } });
    if (result.data && !result.error) {
      return { status: "ok", data: result.data };
    }
    if (result.response.status === 403) return { status: "denied" };
    if (result.response.status === 404) return { status: "not-found" };
    return { status: "error" };
  } catch {
    return { status: "error" };
  }
}

const GENERIC_REGENERATE_ERROR = "Something went wrong regenerating the code. Please try again.";

function messageForRegenerateStatus(status: number): string {
  switch (status) {
    case 403:
      return "Only the doctor who started this handover can regenerate the code.";
    case 404:
      return "This session no longer exists, or you no longer have access to it.";
    case 409:
      // The session isn't active (draft / locked) or the incoming doctor has
      // already used the code — either way a new code can't be minted here.
      return "This handover can no longer regenerate a code — it may be closed or already picked up.";
    default:
      return GENERIC_REGENERATE_ERROR;
  }
}

/**
 * Mint a fresh handover code for an active session
 * (`POST /sessions/:id/code/regenerate`), invalidating the previous one. Like
 * generation, the plaintext `code` comes back exactly once and must live only in
 * component state. Shares {@link GenerateCodeResult} since the success shape is
 * identical.
 */
export async function regenerateHandoverCode(sessionId: string): Promise<GenerateCodeResult> {
  try {
    const result = await sdkRegenerateCode({ path: { id: sessionId } });
    if (result.error || !result.data) {
      return { status: "error", message: messageForRegenerateStatus(result.response.status) };
    }
    return { status: "ok", data: result.data };
  } catch {
    return { status: "error", message: GENERIC_REGENERATE_ERROR };
  }
}

/**
 * The result of a code-validation attempt, broken out so the home screen can
 * render distinct UX for each case (see spec §8 and HAN-54). `error` is the
 * unstructured fallback for unexpected statuses (5xx, network, etc.). The
 * discriminated `status` is what the home route switches on.
 */
export type ValidateCodeResult =
  | { status: "ok"; data: ValidateCodeResponseDto }
  | { status: "wrong"; attemptsLeft?: number }
  | { status: "expired" }
  | { status: "used" }
  | { status: "locked"; retryAfterSeconds: number }
  | { status: "self" }
  | { status: "error" };

/** Default lockout window when neither header nor body carry a usable value. */
const LOCKOUT_FALLBACK_SECONDS = 10 * 60;

/** Read a problem+json body without breaking the original response stream. */
async function readProblemBody(response: Response): Promise<Record<string, unknown> | null> {
  try {
    return (await response.clone().json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Server can carry the lockout window in `Retry-After` (header) or `retryAfter`
 *  (body extension); prefer the header — RFC 7231 — and only fall back when it
 *  isn't a positive integer. Hard floor at 1s so the countdown can never display
 *  a non-positive value. */
function parseRetryAfter(response: Response, body: Record<string, unknown> | null): number {
  const fromHeader = Number.parseInt(response.headers.get("Retry-After") ?? "", 10);
  if (Number.isFinite(fromHeader) && fromHeader > 0) return fromHeader;
  const fromBody = body?.retryAfter;
  if (typeof fromBody === "number" && Number.isFinite(fromBody) && fromBody > 0) {
    return Math.ceil(fromBody);
  }
  return LOCKOUT_FALLBACK_SECONDS;
}

/**
 * Validate a handover code on behalf of the incoming doctor
 * (`POST /sessions/validate-code`). All known error shapes the server returns
 * (E6.3) are mapped to distinct result variants so the home screen renders
 * the right copy without having to know about RFC 7807 extensions.
 *
 * `423` and `410` are thrown as {@link ApiLockedError} / {@link ApiExpiredError}
 * by the shared response interceptor (`~/lib/api.ts`); we catch them here
 * and read the problem+json body to recover the `reason` and `retryAfter`
 * extensions. The `401` wrong-code path is *not* a redirect — the interceptor
 * skips its `/login` navigation when the body carries `reason: "wrong"`.
 */
export async function validateHandoverCode(code: string): Promise<ValidateCodeResult> {
  try {
    const result = await sdkValidateCode({ body: { code } });
    if (result.data && !result.error) {
      return { status: "ok", data: result.data };
    }
    const status = result.response.status;
    if (status === 401) {
      const body = await readProblemBody(result.response);
      if (body?.reason === "wrong") {
        const attemptsLeft = body.attemptsLeft;
        return {
          status: "wrong",
          attemptsLeft: typeof attemptsLeft === "number" ? attemptsLeft : undefined,
        };
      }
      return { status: "error" };
    }
    if (status === 409) {
      const body = await readProblemBody(result.response);
      if (body?.reason === "self_cannot_validate") return { status: "self" };
      return { status: "error" };
    }
    return { status: "error" };
  } catch (err) {
    if (err instanceof ApiLockedError) {
      const body = await readProblemBody(err.response);
      return { status: "locked", retryAfterSeconds: parseRetryAfter(err.response, body) };
    }
    if (err instanceof ApiExpiredError) {
      const body = await readProblemBody(err.response);
      if (body?.reason === "used") return { status: "used" };
      return { status: "expired" };
    }
    return { status: "error" };
  }
}
